// ==============================================
// Admin menu catalog — the code-defined source of truth for every admin
// sidebar destination. The saved menu config (groups/order/visibility)
// references these by `id`. Routes + icons live here in code; the config layer
// only arranges and gates them.
// ==============================================

import type { ComponentType, SVGProps } from 'react';
import {
  HomeIcon,
  BookOpenIcon,
  CubeIcon,
  UsersIcon,
  DevicePhoneMobileIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  KeyIcon,
  BanknotesIcon,
  MapIcon,
  CircleStackIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon,
  FlagIcon,
  ArchiveBoxIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  RectangleGroupIcon,
  CurrencyDollarIcon,
  AdjustmentsHorizontalIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  SparklesIcon,
  TableCellsIcon,
  Squares2X2Icon,
  Square3Stack3DIcon,
  SignalIcon,
  CloudIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

export type MenuVisibility = 'admin' | 'super' | 'hidden';

export interface AdminMenuCatalogItem {
  id: string;
  label: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** NavLink `end` (exact-match) — only the dashboard root needs it. */
  end?: boolean;
  /** Default visibility when the saved config doesn't mention this item. */
  defaultVisibility: MenuVisibility;
}

export const ADMIN_MENU_CATALOG: AdminMenuCatalogItem[] = [
  { id: 'dashboard', label: 'Dashboard', href: '/admin', icon: HomeIcon, end: true, defaultVisibility: 'admin' },
  { id: 'packages', label: 'Packages', href: '/admin/packages', icon: CubeIcon, defaultVisibility: 'admin' },
  { id: 'users', label: 'Users', href: '/admin/users', icon: UsersIcon, defaultVisibility: 'admin' },
  { id: 'fleet-map', label: 'Fleet Map', href: '/admin/fleet-map', icon: MapIcon, defaultVisibility: 'admin' },
  { id: 'fleets', label: 'Fleets', href: '/admin/fleets', icon: BuildingOffice2Icon, defaultVisibility: 'admin' },
  { id: 'devices', label: 'Devices', href: '/admin/devices', icon: DevicePhoneMobileIcon, defaultVisibility: 'admin' },
  { id: 'onboarding', label: 'Scout Onboarding', href: '/admin/onboarding', icon: SparklesIcon, defaultVisibility: 'admin' },
  { id: 'inventory', label: 'Inventory', href: '/admin/inventory', icon: ArchiveBoxIcon, defaultVisibility: 'admin' },
  { id: 'build-station', label: 'Build Station', href: '/admin/build-station', icon: WrenchScrewdriverIcon, defaultVisibility: 'admin' },
  { id: 'hardware-orders', label: 'Hardware Orders', href: '/admin/hardware-orders', icon: ShoppingBagIcon, defaultVisibility: 'admin' },
  { id: 'orders', label: 'Orders', href: '/admin/orders', icon: ShoppingCartIcon, defaultVisibility: 'admin' },
  { id: 'timesheets', label: 'Timesheets', href: '/admin/timesheets', icon: ClockIcon, defaultVisibility: 'admin' },
  { id: 'departments', label: 'Departments', href: '/admin/departments', icon: RectangleGroupIcon, defaultVisibility: 'admin' },
  { id: 'staff', label: 'Staff & Rates', href: '/admin/staff', icon: CurrencyDollarIcon, defaultVisibility: 'admin' },
  { id: 'billing', label: 'Billing Audit', href: '/admin/billing', icon: BanknotesIcon, defaultVisibility: 'admin' },
  { id: 'esri', label: 'Esri Services', href: '/admin/esri', icon: GlobeAltIcon, defaultVisibility: 'admin' },
  { id: 'usage', label: 'Basemap Usage', href: '/admin/usage', icon: TableCellsIcon, defaultVisibility: 'admin' },
  { id: 'tiles', label: 'Map Tiles', href: '/admin/tiles', icon: Square3Stack3DIcon, defaultVisibility: 'admin' },
  { id: 'api-keys', label: 'API Keys', href: '/admin/api-keys', icon: KeyIcon, defaultVisibility: 'admin' },
  { id: 'data-portal', label: 'Data Portal', href: '/admin/data-portal', icon: CircleStackIcon, defaultVisibility: 'admin' },
  { id: 'updates', label: 'Updates', href: '/admin/updates', icon: ArrowDownTrayIcon, defaultVisibility: 'admin' },
  { id: 'app-library', label: 'App Library', href: '/admin/app-library', icon: Squares2X2Icon, defaultVisibility: 'admin' },
  { id: 'ota-releases', label: 'OTA Releases', href: '/admin/releases', icon: RocketLaunchIcon, defaultVisibility: 'admin' },
  { id: 'help', label: 'Help articles', href: '/admin/help', icon: BookOpenIcon, defaultVisibility: 'admin' },
  { id: 'sg-releases', label: 'SG Releases', href: '/admin/sg-releases', icon: ServerStackIcon, defaultVisibility: 'admin' },
  { id: 'weather', label: 'Weather', href: '/admin/weather', icon: CloudIcon, defaultVisibility: 'admin' },
  { id: 'routing', label: 'Routing', href: '/admin/routing', icon: MapIcon, defaultVisibility: 'admin' },
  { id: 'departure-planner', label: 'Departure planner', href: '/admin/departure-planner', icon: EnvelopeIcon, defaultVisibility: 'admin' },
  { id: 'feedback', label: 'Feedback', href: '/admin/feedback', icon: ChatBubbleLeftEllipsisIcon, defaultVisibility: 'admin' },
  { id: 'nav-library-reports', label: 'Shared library reports', href: '/admin/nav-library-reports', icon: FlagIcon, defaultVisibility: 'admin' },
  { id: 'paper-library-reports', label: 'Document library reports', href: '/admin/paper-library-reports', icon: FlagIcon, defaultVisibility: 'admin' },
  { id: 'passage-reports', label: 'PassageWiki reports', href: '/admin/passage-reports', icon: FlagIcon, defaultVisibility: 'admin' },
  { id: 'analytics', label: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon, defaultVisibility: 'admin' },
  { id: 'settings', label: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon, defaultVisibility: 'admin' },
  { id: 'emulator', label: 'NMEA Studio', href: '/admin/emulator', icon: SignalIcon, defaultVisibility: 'super' },
  { id: 'menu-builder', label: 'Menu Builder', href: '/admin/menu-builder', icon: AdjustmentsHorizontalIcon, defaultVisibility: 'super' },
];

export const ADMIN_MENU_BY_ID: Record<string, AdminMenuCatalogItem> = Object.fromEntries(
  ADMIN_MENU_CATALOG.map((i) => [i.id, i]),
);

// ---- Config types (mirror vortex-api/src/types AdminMenuConfig) ----

export interface MenuItemNode {
  type: 'item';
  id: string;
  visibility: MenuVisibility;
}
export interface MenuGroupNode {
  type: 'group';
  id: string;
  title: string;
  collapsed?: boolean;
  items: { id: string; visibility: MenuVisibility }[];
}
export type MenuNode = MenuItemNode | MenuGroupNode;
export interface AdminMenuConfig {
  version: 1;
  nodes: MenuNode[];
}

/**
 * Build the default config (flat list of all catalog items, no groups) — used
 * when nothing has been saved yet, and as the seed for the editor.
 */
export function defaultMenuConfig(): AdminMenuConfig {
  return {
    version: 1,
    nodes: ADMIN_MENU_CATALOG.map((i) => ({ type: 'item', id: i.id, visibility: i.defaultVisibility })),
  };
}

/**
 * Normalize a saved config against the current catalog: drop unknown ids and
 * append any catalog items not present yet as loose items (so newly-added
 * screens always surface). Returns a config safe to render/edit.
 */
export function reconcileMenuConfig(config: AdminMenuConfig | null | undefined): AdminMenuConfig {
  if (!config || !Array.isArray(config.nodes)) return defaultMenuConfig();

  const seen = new Set<string>();
  const nodes: MenuNode[] = [];
  for (const node of config.nodes) {
    if (node.type === 'item') {
      if (ADMIN_MENU_BY_ID[node.id] && !seen.has(node.id)) {
        seen.add(node.id);
        nodes.push({ type: 'item', id: node.id, visibility: node.visibility });
      }
    } else if (node.type === 'group') {
      const items = (node.items ?? []).filter((it) => ADMIN_MENU_BY_ID[it.id] && !seen.has(it.id));
      items.forEach((it) => seen.add(it.id));
      nodes.push({ type: 'group', id: node.id, title: node.title, collapsed: node.collapsed, items });
    }
  }
  // Append catalog items missing from the saved config as loose items.
  for (const item of ADMIN_MENU_CATALOG) {
    if (!seen.has(item.id)) nodes.push({ type: 'item', id: item.id, visibility: item.defaultVisibility });
  }
  return { version: 1, nodes };
}
